import { onUnmounted, readonly, ref } from "vue";
import { useRouter } from "vue-router";

import { useAuthStore } from "@/stores/authStore";

const REFRESH_BUFFER_MINUTES = 10; // Refresh 10 minutes before expiry
const CHECK_INTERVAL_MS = 60000; // Check every minute

export const useTokenManager = () => {
  const authStore = useAuthStore();
  const router = useRouter();
  let checkInterval = null;
  const isRefreshing = ref(false);

  const parseExpirationTime = (expiresAt) => {
    if (!expiresAt) return null;

    // Handle different timestamp formats
    if (typeof expiresAt === "number") {
      return new Date(expiresAt * 1000); // Unix timestamp
    }

    // Handle SQL datetime format: "2025-07-30 16:17:52"
    if (typeof expiresAt === "string" && expiresAt.includes(" ")) {
      // Replace space with 'T' to make it ISO-compatible
      const isoString = expiresAt.replace(" ", "T");
      return new Date(isoString);
    }

    return new Date(expiresAt); // ISO string or other formats
  };

  const shouldRefreshToken = () => {
    if (!authStore.isAuthenticated || !authStore.expiresAt) {
      return false;
    }

    const expirationTime = parseExpirationTime(authStore.expiresAt);
    if (!expirationTime) return false;

    const now = new Date();
    const timeUntilExpiry = expirationTime.getTime() - now.getTime();
    const bufferTime = REFRESH_BUFFER_MINUTES * 60 * 1000;

    return timeUntilExpiry <= bufferTime && timeUntilExpiry > 0;
  };

  const isTokenExpired = () => {
    if (!authStore.isAuthenticated || !authStore.expiresAt) {
      return false;
    }

    const expirationTime = parseExpirationTime(authStore.expiresAt);
    if (!expirationTime) return false;

    return new Date() >= expirationTime;
  };

  const handleExpiredToken = () => {
    console.log("Token expired, clearing auth data and redirecting to login");
    authStore.clearAuthData();

    // Only redirect if not already on login/auth pages
    const currentRoute = router.currentRoute.value;
    const authRoutes = ["login", "forgotPassword", "resetPassword", "acceptInvitation"];

    if (!authRoutes.includes(currentRoute.name)) {
      router.push({ name: "login" });
    }
  };

  const refreshTokenIfNeeded = async () => {
    if (isRefreshing.value) {
      return; // Prevent multiple simultaneous refresh attempts
    }

    if (isTokenExpired()) {
      handleExpiredToken();
      return;
    }

    if (shouldRefreshToken()) {
      isRefreshing.value = true;

      try {
        console.log("Refreshing token...");
        await authStore.refreshToken();
        console.log("Token refreshed successfully");
      } catch (error) {
        console.error("Failed to refresh token:", error);
        handleExpiredToken();
      } finally {
        isRefreshing.value = false;
      }
    }
  };

  const startTokenMonitoring = () => {
    if (checkInterval) {
      window.clearInterval(checkInterval);
    }

    // Initial check
    refreshTokenIfNeeded();

    // Set up periodic checks
    checkInterval = window.setInterval(refreshTokenIfNeeded, CHECK_INTERVAL_MS);
  };

  const stopTokenMonitoring = () => {
    if (checkInterval) {
      window.clearInterval(checkInterval);
      checkInterval = null;
    }
  };

  // Cleanup on component unmount
  onUnmounted(() => {
    stopTokenMonitoring();
  });

  return {
    startTokenMonitoring,
    stopTokenMonitoring,
    refreshTokenIfNeeded,
    isRefreshing: readonly(isRefreshing),
  };
};
